import React, { useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@roger/ui-components';

import type { WoundImage } from './useWoundImages';

interface WoundImageLightboxProps {
  images: WoundImage[];
  /** Position in `images` to show; `null` closes the gallery. */
  index: number | null;
  onIndexChange: (index: number) => void;
  onClose: () => void;
}

const WoundImageLightbox: React.FC<WoundImageLightboxProps> = ({
  images,
  index,
  onIndexChange,
  onClose,
}) => {
  const isOpen = index !== null && index >= 0 && index < images.length;
  const current = isOpen ? images[index] : null;
  const canNavigate = images.length > 1;

  // Wrapping keeps both arrows live, which suits a handful of photos better
  // than ends that go dead without explaining why.
  const step = (delta: number) => {
    if (index === null) return;
    onIndexChange((index + delta + images.length) % images.length);
  };

  useEffect(() => {
    if (!isOpen || !canNavigate) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') step(-1);
      if (event.key === 'ArrowRight') step(1);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  });

  return (
    <Dialog
      open={isOpen}
      onOpenChange={open => {
        if (!open) onClose();
      }}
    >
      <DialogContent className='w-[min(95vw,1200px)] max-w-[1200px] max-h-[90vh] p-2 sm:p-4'>
        <DialogHeader>
          <DialogTitle className='text-sm font-medium'>
            {current?.fileName ?? ''}
            {canNavigate && index !== null && (
              <span className='ml-2 font-normal text-muted-foreground'>
                {index + 1} of {images.length}
              </span>
            )}
          </DialogTitle>
          <DialogDescription className='sr-only'>
            {canNavigate
              ? 'Wound photo. Use the left and right arrow keys to move between photos.'
              : 'Wound photo.'}
          </DialogDescription>
        </DialogHeader>
        {current && (
          <div className='flex items-center justify-center gap-2 overflow-auto'>
            {canNavigate && (
              <Button
                variant='outline'
                size='sm'
                className='shrink-0'
                onClick={() => step(-1)}
                aria-label='Previous photo'
              >
                <ChevronLeft className='h-4 w-4' />
              </Button>
            )}
            <img
              src={current.url}
              alt={current.fileName}
              className='max-h-[80vh] max-w-full object-contain'
            />
            {canNavigate && (
              <Button
                variant='outline'
                size='sm'
                className='shrink-0'
                onClick={() => step(1)}
                aria-label='Next photo'
              >
                <ChevronRight className='h-4 w-4' />
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WoundImageLightbox;
